/* frontend/src/config/apiConfig.js - 통합 API 설정 */
import axios from 'axios';
// 🔥 CRITICAL FIX: 정적 import로 변경하여 TDZ 오류 해결
import { app } from '../firebase/firebaseConfig.jsx';

// 환경 변수 기반 API 설정
const getApiConfig = () => {
  const env = import.meta.env.MODE;
  
  // 환경 변수에서 백엔드 URL 가져오기 (우선순위: VITE_API_BASE_URL > 기본값)
  const backendBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';
  
  const configs = {
    development: {
      frontend: {
        port: 3011,
        baseUrl: 'http://localhost:3011'
      },
      backend: {
        port: 8080,
        baseUrl: backendBaseUrl
      },
      admin: {
        port: 3002,
        baseUrl: 'http://localhost:3002'
      }
    },
    production: {
      frontend: {
        port: 443,
        baseUrl: 'https://kairos-frontend.com'
      },
      backend: {
        port: 443,
        baseUrl: backendBaseUrl || 'https://api.kairos-global-k.com'
      },
      admin: {
        port: 443,
        baseUrl: 'https://admin.kairos.com'
      }
    }
  };

  return configs[env] || configs.development;
};

const apiConfig = getApiConfig();

// 백엔드 URL 로깅 (디버깅용 - 개발 환경에서만)
if (import.meta.env.MODE === 'development') {
  console.log('🔍 API Config:', {
    mode: import.meta.env.MODE,
    backendBaseUrl: apiConfig.backend.baseUrl,
    viteApiBaseUrl: import.meta.env.VITE_API_BASE_URL
  });
}

// API 엔드포인트 상수
export const API_ENDPOINTS = {
  // 인증
  AUTH: {
    LOGIN: '/api/auth/login',
    LOGOUT: '/api/auth/logout',
    REGISTER: '/api/auth/register',
    REFRESH: '/api/auth/refresh',
    VERIFY: '/api/auth/verify'
  },
  
  // 사용자
  USER: {
    PROFILE: '/api/users/profile',
    UPDATE: '/api/users/update',
    FCM_TOKEN: '/api/users/fcm-token',
    NOTIFICATIONS: '/api/users/notifications'
  },
  
  // 송금
  REMITTANCE: {
    REQUEST: '/api/remittances/request',
    HISTORY: '/api/remittances/history',
    STATUS: '/api/remittances/status',
    CANCEL: '/api/remittances/cancel'
  },
  
  // KYC
  KYC: {
    SUBMIT: '/api/kyc/submit',
    STATUS: '/api/kyc/status',
    DOCUMENTS: '/api/kyc/documents'
  },
  
  // 파일 업로드
  STORAGE: {
    UPLOAD_URL: '/api/storage/upload-url',
    DELETE: '/api/storage/delete'
  },
  
  // 공지사항
  NOTICES: {
    LIST: '/api/notices',
    DETAIL: '/api/notices/detail',
    CREATE: '/api/notices/create',
    UPDATE: '/api/notices/update',
    DELETE: '/api/notices/delete'
  }
};

// API 클라이언트 설정
// 환경 변수를 직접 사용하여 명시적으로 설정
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

if (import.meta.env.MODE === 'development') {
  console.log('🔍 API_BASE_URL 설정:', API_BASE_URL);
  console.log('🔍 import.meta.env.VITE_API_BASE_URL:', import.meta.env.VITE_API_BASE_URL);
}

export const API_CONFIG = {
  baseURL: API_BASE_URL,
  timeout: 120000, // 120초 (2분) - Gemini API 응답 시간이 오래 걸릴 수 있음
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
};

// Axios 인스턴스 생성
export const apiClient = axios.create(API_CONFIG);

// 요청 인터셉터 - 토큰 자동 추가 및 baseURL 강제 설정
apiClient.interceptors.request.use(
  async (config) => {
    try {
      // baseURL 강제 설정 (환경 변수 우선)
      const correctBaseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';
      config.baseURL = correctBaseURL;
      
      // baseURL 확인 및 로깅 (개발 환경에서만)
      if (import.meta.env.MODE === 'development') {
        console.log('🔍 요청 URL 확인:', {
          originalBaseURL: config.baseURL,
          correctedBaseURL: correctBaseURL,
          url: config.url,
          fullURL: correctBaseURL + (config.url || ''),
          envViteApiBaseUrl: import.meta.env.VITE_API_BASE_URL
        });
      }
      
      // Firebase Auth에서 현재 사용자 가져오기
      // 🔥 CRITICAL FIX: 정적 import 사용 (이미 상단에서 import됨)
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth(app);
      const user = auth.currentUser;
      
      if (user) {
        const idToken = await user.getIdToken();
        config.headers.Authorization = `Bearer ${idToken}`;
        if (import.meta.env.MODE === 'development') {
          console.log('🔍 API 요청에 토큰 추가됨:', idToken.substring(0, 20) + '...');
        }
      }
    } catch (error) {
      if (import.meta.env.MODE === 'development') {
        console.error('❌ 토큰 가져오기 실패:', error);
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터 - 에러 처리
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    if (error.response?.status === 401) {
      if (import.meta.env.MODE === 'development') {
        console.log('🔒 인증 오류 발생 - 로그인 페이지로 리다이렉트');
      }
      // 토큰 만료 시 로그아웃 처리
      // 🔥 CRITICAL FIX: 정적 import 사용 (이미 상단에서 import됨)
      try {
        const { getAuth } = await import('firebase/auth');
        const auth = getAuth(app);
        await auth.signOut();
      } catch (signOutError) {
        if (import.meta.env.MODE === 'development') {
          console.error('❌ 로그아웃 실패:', signOutError);
        }
      }
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// 환경별 설정
export const ENV_CONFIG = {
  isDevelopment: import.meta.env.MODE === 'development',
  isProduction: import.meta.env.MODE === 'production',
  apiBaseUrl: apiConfig.backend.baseUrl,
  frontendUrl: apiConfig.frontend.baseUrl,
  adminUrl: apiConfig.admin.baseUrl
};

export default apiConfig;
